/**
 * Compile a token list into a Labs QR payload, with validation.
 *
 * The lint rules below encode Labs' own footguns: `!` followed by a lowercase
 * letter is invalid, a shutdown inside a repeating script kills the loop, `!nE`
 * does nothing in single-photo mode, curly quotes will not parse. These are the
 * errors the GoPro docs call out explicitly, which means they are the errors
 * people actually make.
 */

import { supportedBy } from "./cameras";
import type { Camera } from "./cameras";
import type { DiveConditions } from "./conditions";
import { buildAdvancedTokens, buildTokens, cleanMacroName, restoreMacroName } from "./engine";
import { MAX_SINGLE_QR_CHARS, tokenSupported, type Token } from "./tokens";

/** Shared wording for "this token needs a camera feature this one doesn't
 *  have" — used both for tokens dropped from a plain scanned code and for
 *  tokens dropped while assembling a dial-mode macro, so the two paths can't
 *  drift into describing the same drop differently. */
function unsupportedDiagnostic(token: Token, destination: string): Diagnostic {
  return {
    level: "warning",
    message:
      `${token.label || token.text} (${token.text}) needs ` +
      `${token.feature ? supportedBy(token.feature) : "another camera"}, ` +
      `so it was left out of ${destination}.`,
  };
}

export type DiagnosticLevel = "error" | "warning";

export interface Diagnostic {
  level: DiagnosticLevel;
  message: string;
  tokenIndex?: number;
}

export interface CompileResult {
  payload: string;
  /** Tokens that actually made it into the payload, in order. */
  emitted: Token[];
  /** Tokens dropped because this camera cannot act on them. */
  omitted: Token[];
  charCount: number;
  charBudget: number;
  diagnostics: Diagnostic[];
  ok: boolean;
}

// Labs rejects anything starting with an uppercase letter, and `!` is only ever
// followed by an uppercase action letter or a time.
const VALID_START = /^[a-z$*!"=<>^~+]/;
const BAD_ACTION = /![a-z]/;

export function compile(tokens: readonly Token[], camera: Camera): CompileResult {
  const diagnostics: Diagnostic[] = [];
  const emitted: Token[] = [];
  const omitted: Token[] = [];

  tokens.forEach((token, i) => {
    if (!tokenSupported(token, camera)) {
      // Dropped rather than emitted: a token the camera ignores is pure
      // character budget spent on nothing. Dropping it is only acceptable
      // because we say so, loudly, right here.
      omitted.push(token);
      diagnostics.push({ ...unsupportedDiagnostic(token, `the code for ${camera}`), tokenIndex: i });
      return;
    }

    if (!VALID_START.test(token.text)) {
      diagnostics.push({
        level: "error",
        tokenIndex: i,
        message:
          `"${token.text}" starts with an uppercase letter. Labs tokens start ` +
          `with a lowercase letter or one of $ * ! " = < > ^`,
      });
    }
    if (BAD_ACTION.test(token.text)) {
      diagnostics.push({
        level: "error",
        tokenIndex: i,
        message:
          `"${token.text}" uses '!' before a lowercase letter. Actions are ` +
          "uppercase, e.g. !S, !60E, !R",
      });
    }

    emitted.push(token);
  });

  const payload = emitted.map((t) => t.text).join("");
  diagnostics.push(...lintScript(payload));

  if (payload.length > MAX_SINGLE_QR_CHARS) {
    diagnostics.push({
      level: "error",
      message:
        `${payload.length} characters exceeds the ${MAX_SINGLE_QR_CHARS} character ` +
        "limit for one code. Split it, or move part of it into a macro stored " +
        'with *FOUR="...".',
    });
  } else if (payload.length > MAX_SINGLE_QR_CHARS * 0.85) {
    diagnostics.push({
      level: "warning",
      message:
        `${payload.length} of ${MAX_SINGLE_QR_CHARS} characters used. Dense codes ` +
        "are harder for the camera to read underwater.",
    });
  }

  return {
    payload,
    emitted,
    omitted,
    charCount: payload.length,
    charBudget: MAX_SINGLE_QR_CHARS,
    diagnostics,
    ok: !diagnostics.some((d) => d.level === "error"),
  };
}

/** Rules that only make sense against the whole string. */
function lintScript(payload: string): Diagnostic[] {
  const out: Diagnostic[] = [];

  const repeats = /!\d*:?\d*RQ?/.test(payload);
  const shutdown = /!\d*:?\d*O(?!R)/.test(payload);
  if (repeats && shutdown) {
    out.push({
      level: "error",
      message:
        "A shutdown (!O) inside a repeating script kills the loop. Use a sleep (!30N) instead.",
    });
  }
  if (payload.includes("mP") && /!\d+E/.test(payload)) {
    out.push({
      level: "warning",
      message: "End actions (!nE) do nothing in single-photo mode. Use !nN if you wanted a pause.",
    });
  }
  if ((payload.match(/"/g) ?? []).length % 2) {
    out.push({ level: "error", message: "Unbalanced quotes." });
  }
  if (payload.includes("“") || payload.includes("”")) {
    out.push({
      level: "error",
      message: "Curly quotes will not parse. Use straight ASCII quotes.",
    });
  }
  out.push(...lintPresetName(payload));
  return out;
}

/** Every FourCC in GoPro's own reference (gopro.github.io/labs/control/tech/),
 *  pulled directly from the page text rather than guessed. A macro name that
 *  collides with one of these does not just risk confusing a diver reading a
 *  preset name (that is `lintPresetName`'s job, below, and a warning) — it
 *  risks the macro itself shadowing a real Labs command, which is a hard
 *  error in `compileAdvanced`. Single source of truth for both: a name that
 *  is reserved enough to hard-error as a macro key is reserved enough to
 *  warn about inside free-text preset names too. */
const RESERVED_FOURCC = new Set([
  "ARCH", "AUDS", "BASE", "BERS", "BFLK", "BIAS", "BITH", "BITL", "BITR",
  "BOOT", "BRNP", "BRNT", "BRNX", "BURN", "BYPS", "CBAR", "COOL", "DAMP",
  "DAUD", "DBGL", "DECR", "DIST", "DIVE", "DLRV", "DSPC", "DSPL", "ENCR",
  "EVBS", "EXPN", "EXPQ", "EXPS", "EXPT", "EXPX", "FAST", "FEAT", "GAIN",
  "GCMP", "GPSL", "GUIC", "GUID", "HDMI", "HIST", "HNDL", "HSTO", "HSTP",
  "IWFR", "JOIN", "LBAR", "LEVL", "LLTZ", "LOGB", "LOGO", "MUTE", "OLTC",
  "OWNR", "PMSG", "PRES", "PRXY", "QRDR", "RLTC", "RTMP", "SHMX", "SOLO",
  "SPED", "STOP", "SYNC", "TCAL", "TONE", "TUSB", "WAKE", "WARM", "WBDV",
  "WBLK", "WIDE", "ZONE",
]);

/**
 * A preset name is not inert text.
 *
 * From GoPro's reference: "preset name that use FourCC commands will enable
 * those commands only when the user selects that preset" — `$PRES="14,DIVE
 * WBLK"` makes a preset that arms DIVE and WBLK. Labs also reads `B001`–`B999`
 * in a name as a bitrate and `NR01`–`NR99` as a noise-reduction percentage.
 *
 * So a diver naming a preset after the dive can change what the camera does,
 * with nothing in the "why these settings" panel to explain it. That is the
 * precise failure this app exists to prevent, so it gets flagged here.
 */
function lintPresetName(payload: string): Diagnostic[] {
  const name = /\$PRES="\d+,([^"]*)"/.exec(payload)?.[1];
  if (!name) return [];

  const hits = [
    ...[...RESERVED_FOURCC].filter((code) => new RegExp(`\\b${code}\\b`).test(name)),
    ...(name.match(/\bB\d{3}\b/g) ?? []).map((m) => `${m} (sets bitrate)`),
    ...(name.match(/\bNR\d{2}\b/g) ?? []).map((m) => `${m} (sets noise reduction)`),
  ];
  if (!hits.length) return [];

  return [
    {
      level: "warning",
      message:
        `The preset name “${name}” contains ${hits.join(", ")}. Labs reads ` +
        "command codes inside a preset name and applies them whenever that " +
        "preset is selected, so this name changes what the camera does. " +
        "Rename it unless you meant that.",
    },
  ];
}

/**
 * Advice that is about the dive rather than about the syntax. Kept separate
 * because these are judgements, not rules — and because the app should never
 * imply more certainty than it has.
 */
export function adviseOnConditions(c: DiveConditions): Diagnostic[] {
  const out: Diagnostic[] = [];

  if (c.persistenceMode === "permanent") {
    out.push({
      level: "warning",
      message:
        "Permanent settings stay on the camera after this dive too — through " +
        "reboots, battery swaps, different SD cards — until you scan a code " +
        "with Persistence set back to “Until power off”, or send !RESET!1OR. " +
        "Good for a boat that reuses the same rig all week; easy to forget by " +
        "the next one.",
    });
  }
  if (c.persistenceMode === "dial") {
    out.push({
      level: "warning",
      message:
        "Tied-to-preset settings only change when you pick a different named " +
        "preset from the camera's own dial or menu — not by scanning another " +
        "code, and not by rebooting. Selecting this preset re-applies its " +
        "settings every time; selecting a different preset restores the " +
        "camera to its defaults first.",
    });
  }
  if (c.depthM > 25 && c.light === "ambient") {
    out.push({
      level: "warning",
      message:
        "Below about 25 m there is no red left in ambient light to recover. " +
        "Digital gain amplifies noise instead of colour. Lights are the only real fix.",
    });
  }
  if (c.workflow === "graded" && c.subject === "surface") {
    out.push({
      level: "warning",
      message:
        "Flat plus 10-bit for a snorkel clip is a lot of grading for footage " +
        "that already has its colour.",
    });
  }
  if (c.workflow === "graded") {
    out.push({
      level: "warning",
      message:
        "120 Mb/s assumes a card rated V30 or better. Bad cards fail mid-dive, " +
        "which is the worst possible time.",
    });
  }
  if (c.depthM > 20 && c.frameRate > 30) {
    out.push({
      level: "warning",
      message:
        `${c.frameRate} fps at ${c.depthM} m costs stops of light you don't have ` +
        "this deep. Not recommended — consider dropping to 30 fps.",
    });
  }
  return out;
}

export interface AdvancedCompileResult {
  macroName: string;
  restoreName: string;
  /** Core settings, the setup macro (`*<macroName>="..."`, runs on select),
   *  the restore macro (`*<restoreName>="..."`, runs on deselect), and
   *  `$PRES="<icon>,<macroName>"` — all in one scanned code, same shape as
   *  the plain `compile()` path. Measured across the full condition space
   *  (see git history), the combined payload tops out around 180 of the
   *  255-character budget, so there is no technical reason to split this
   *  across multiple codes — a diver would otherwise have to scan three
   *  separate QR codes in a specific order for one preset. */
  code: CompileResult;
}

/**
 * Compiles the "tied to this preset" (`persistenceMode: "dial"`) path into
 * one payload. Built on top of `compile()` rather than beside it — each
 * macro body is wrapped as one synthetic token and run through the same
 * pipeline, so quote-balance, curly-quote, and length checks apply for free
 * instead of being reimplemented here.
 */
/** An empty macro name has no valid rendering — `"____"` was previously used
 *  as a display-only stand-in for the preset step while the setup/restore
 *  macros were still keyed on the real (empty) name, which produced invalid
 *  `*="..."` tokens. Simpler and correct: an invalid name means no payload
 *  at all, on any of the three steps, until it's fixed. */
const EMPTY_RESULT: CompileResult = {
  payload: "",
  emitted: [],
  omitted: [],
  charCount: 0,
  charBudget: MAX_SINGLE_QR_CHARS,
  diagnostics: [],
  ok: false,
};

function macroToken(name: string, body: readonly Token[], label: string, rationale: string): Token {
  return {
    text: `*${name}="${body.map((t) => t.text).join("")}"`,
    kind: "ext_perm",
    label,
    rationale,
  };
}

/** Extension tokens are individually feature-gated (see `tokens.ts`), but
 *  once flattened into one macro string that per-token gating has nothing
 *  left to check against — the macro itself is just text. So the filtering
 *  has to happen here, before flattening, using the same camera-support
 *  check and the same dropped-token wording `compile()` uses for the plain
 *  path. */
function filterForCamera(
  tokens: readonly Token[],
  camera: Camera,
  destination: string,
): { kept: Token[]; dropped: Diagnostic[] } {
  const kept: Token[] = [];
  const dropped: Diagnostic[] = [];
  tokens.forEach((token) => {
    if (tokenSupported(token, camera)) {
      kept.push(token);
    } else {
      dropped.push(unsupportedDiagnostic(token, destination));
    }
  });
  return { kept, dropped };
}

/** Both invalid-name cases below produce the same shape — no payload on any
 *  step, one error diagnostic — differing only in the message. */
function invalidMacroResult(macroName: string, restoreName: string, message: string): AdvancedCompileResult {
  return {
    macroName,
    restoreName,
    code: { ...EMPTY_RESULT, diagnostics: [{ level: "error", message }] },
  };
}

export function compileAdvanced(c: DiveConditions): AdvancedCompileResult {
  const macroName = cleanMacroName(c.presetName ?? "");
  const restoreName = restoreMacroName(macroName);

  if (macroName.length < 1) {
    return invalidMacroResult(
      macroName,
      restoreName,
      "Name this preset (1-4 letters) before using a tied-to-preset persistence mode.",
    );
  }
  if (RESERVED_FOURCC.has(macroName)) {
    return invalidMacroResult(
      macroName,
      restoreName,
      `“${macroName}” is already a GoPro Labs command. A tied-to-preset name ` +
        "doubles as a macro key, so it can't collide with a real FourCC — that " +
        "would shadow the real command instead of just naming a dial preset. " +
        "Pick a different 4-letter code.",
    );
  }

  const { core, setup, restore, preset } = buildAdvancedTokens(c, macroName);

  const setupFiltered = filterForCamera(setup, c.camera, `the “${macroName}” setup macro for ${c.camera}`);
  const restoreFiltered = filterForCamera(
    restore,
    c.camera,
    `the “${restoreName}” restore macro for ${c.camera}`,
  );

  const codeResult = compile(
    [
      ...core,
      macroToken(
        macroName,
        setupFiltered.kept,
        "Setup macro",
        `Runs when “${macroName}” is selected on the dial.`,
      ),
      macroToken(
        restoreName,
        restoreFiltered.kept,
        "Restore macro",
        `Runs when you switch away from “${macroName}” to a different preset.`,
      ),
      preset,
    ],
    c.camera,
  );

  return {
    macroName,
    restoreName,
    code: {
      ...codeResult,
      diagnostics: [...setupFiltered.dropped, ...restoreFiltered.dropped, ...codeResult.diagnostics],
    },
  };
}

/** The one routing decision "which compiler does this preset go through" —
 *  shared by the live builder and the shelf, so the two can't silently
 *  diverge on what a saved preset actually compiles to. */
export function compileConditions(c: DiveConditions): CompileResult {
  return c.persistenceMode === "dial" ? compileAdvanced(c).code : compile(buildTokens(c), c.camera);
}
